import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, Plus, Building2, Phone, Mail, CreditCard, 
  DollarSign, FileText, Eye, Edit2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SuppliersPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    total_proveedores: 0, proveedores_activos: 0, total_saldo_pendiente: 0,
    proveedores_con_saldo: 0, compras_este_mes: 0, monto_compras_mes: 0
  });
  const [suppliers, setSuppliers] = useState([]);
  const [constants, setConstants] = useState({ payment_conditions: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBalance, setFilterBalance] = useState('');

  // Modals
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPayables, setSupplierPayables] = useState([]);

  // Form
  const [formData, setFormData] = useState({
    nombre_comercial: '', razon_social: '', nit: '', direccion: '',
    telefono: '', telefono_secundario: '', email: '', contacto_principal: '',
    condicion_pago_default: 'contado', limite_credito: '', notas: ''
  });

  // Fetch data
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/suppliers/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/suppliers/?`;
      if (filterBalance === 'con_saldo') url += `con_saldo=true&`;
      if (searchQuery) url += `search=${searchQuery}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filterBalance, searchQuery]);

  const fetchConstants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/suppliers/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Error fetching constants:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchConstants();
  }, [fetchStats, fetchConstants]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Create supplier
  const handleCreate = async () => {
    if (!formData.nombre_comercial) {
      toast.error('El nombre comercial es requerido');
      return;
    }
    try {
      await axios.post(`${API}/suppliers/`, {
        ...formData,
        limite_credito: formData.limite_credito ? parseFloat(formData.limite_credito) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proveedor creado');
      setShowNewModal(false);
      resetForm();
      fetchSuppliers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear proveedor');
    }
  };

  // Update supplier
  const handleUpdate = async () => {
    if (!formData.nombre_comercial) {
      toast.error('El nombre comercial es requerido');
      return;
    }
    try {
      await axios.put(`${API}/suppliers/${selectedSupplier.id}`, {
        ...formData,
        limite_credito: formData.limite_credito ? parseFloat(formData.limite_credito) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proveedor actualizado');
      setShowEditModal(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  // View supplier details
  const handleViewDetail = async (supplier) => {
    setSelectedSupplier(supplier);
    try {
      const response = await axios.get(`${API}/suppliers/${supplier.id}/payables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplierPayables(response.data);
    } catch (error) {
      setSupplierPayables([]);
    }
    setShowDetailModal(true);
  };

  // Open edit modal
  const openEditModal = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      nombre_comercial: supplier.nombre_comercial || '',
      razon_social: supplier.razon_social || '',
      nit: supplier.nit || '',
      direccion: supplier.direccion || '',
      telefono: supplier.telefono || '',
      telefono_secundario: supplier.telefono_secundario || '',
      email: supplier.email || '',
      contacto_principal: supplier.contacto_principal || '',
      condicion_pago_default: supplier.condicion_pago_default || 'contado',
      limite_credito: supplier.limite_credito || '',
      notas: supplier.notas || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      nombre_comercial: '', razon_social: '', nit: '', direccion: '',
      telefono: '', telefono_secundario: '', email: '', contacto_principal: '',
      condicion_pago_default: 'contado', limite_credito: '', notas: ''
    });
    setSelectedSupplier(null);
  };

  const formatCurrency = (value) => `Q ${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-slate-900">Proveedores</h1>
          <p className="text-slate-500 text-sm">Gestión de proveedores y condiciones de pago</p>
        </div>
        <Button onClick={() => setShowNewModal(true)} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-display font-medium text-slate-900">{stats.proveedores_activos}</div>
                <div className="text-sm text-slate-500">Proveedores activos</div>
              </div>
              <Building2 className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-display font-medium text-amber-700">{formatCurrency(stats.total_saldo_pendiente)}</div>
                <div className="text-sm text-amber-600">Saldo pendiente total</div>
              </div>
              <CreditCard className="w-10 h-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-display font-medium text-slate-900">{stats.compras_este_mes}</div>
                <div className="text-sm text-slate-500">Compras este mes</div>
              </div>
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-teal-200 bg-teal-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-display font-medium text-teal-700">{formatCurrency(stats.monto_compras_mes)}</div>
                <div className="text-sm text-teal-600">Monto compras mes</div>
              </div>
              <DollarSign className="w-10 h-10 text-teal-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre o NIT..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-suppliers"
          />
        </div>
        <Select value={filterBalance || "todos"} onValueChange={setFilterBalance}>
          <SelectTrigger className="w-48" data-testid="filter-balance">
            <SelectValue placeholder="Filtrar por saldo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="con_saldo">Con saldo pendiente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Suppliers Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Proveedor</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">NIT</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Contacto</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Condición Pago</th>
                <th className="text-right p-4 text-sm font-medium text-slate-600">Saldo Pendiente</th>
                <th className="text-center p-4 text-sm font-medium text-slate-600">Compras</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-500">Cargando...</td>
                </tr>
              ) : suppliers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-500">No hay proveedores</td>
                </tr>
              ) : (
                suppliers.map((supplier) => (
                  <tr key={supplier.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{supplier.nombre_comercial}</div>
                      {supplier.razon_social && supplier.razon_social !== supplier.nombre_comercial && (
                        <div className="text-xs text-slate-500">{supplier.razon_social}</div>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-sm text-slate-600">{supplier.nit || '-'}</span>
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        {supplier.telefono && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Phone className="w-3 h-3" />
                            {supplier.telefono}
                          </div>
                        )}
                        {supplier.email && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Mail className="w-3 h-3" />
                            {supplier.email}
                          </div>
                        )}
                        {supplier.contacto_principal && (
                          <div className="text-xs text-slate-500">{supplier.contacto_principal}</div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="text-slate-600">
                        {supplier.condicion_pago_label}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-medium ${supplier.saldo_pendiente_total > 0 ? 'text-amber-700' : 'text-slate-600'}`}>
                        {formatCurrency(supplier.saldo_pendiente_total)}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-slate-600">{supplier.total_compras}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetail(supplier)}
                          title="Ver detalle"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(supplier)}
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* New/Edit Supplier Modal */}
      <Dialog open={showNewModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowNewModal(false);
          setShowEditModal(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {showEditModal ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre comercial *</Label>
                <Input
                  value={formData.nombre_comercial}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre_comercial: e.target.value }))}
                  placeholder="Nombre del proveedor"
                  data-testid="supplier-name"
                />
              </div>
              <div>
                <Label>Razón social</Label>
                <Input
                  value={formData.razon_social}
                  onChange={(e) => setFormData(prev => ({ ...prev, razon_social: e.target.value }))}
                  placeholder="Razón social"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>NIT</Label>
                <Input
                  value={formData.nit}
                  onChange={(e) => setFormData(prev => ({ ...prev, nit: e.target.value }))}
                  placeholder="123456-7"
                  data-testid="supplier-nit"
                />
              </div>
              <div>
                <Label>Contacto principal</Label>
                <Input
                  value={formData.contacto_principal}
                  onChange={(e) => setFormData(prev => ({ ...prev, contacto_principal: e.target.value }))}
                  placeholder="Nombre del contacto"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono principal</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                  placeholder="+502 XXXX-XXXX"
                />
              </div>
              <div>
                <Label>Teléfono secundario</Label>
                <Input
                  value={formData.telefono_secundario}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefono_secundario: e.target.value }))}
                  placeholder="+502 XXXX-XXXX"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Correo electrónico</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="proveedor@empresa.com"
                />
              </div>
              <div>
                <Label>Dirección</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData(prev => ({ ...prev, direccion: e.target.value }))}
                  placeholder="Dirección fiscal"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Condición de pago</Label>
                <Select 
                  value={formData.condicion_pago_default} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, condicion_pago_default: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {constants.payment_conditions.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Límite de crédito (Q)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.limite_credito}
                  onChange={(e) => setFormData(prev => ({ ...prev, limite_credito: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Observaciones adicionales..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewModal(false); setShowEditModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button 
              onClick={showEditModal ? handleUpdate : handleCreate} 
              className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
            >
              {showEditModal ? 'Guardar Cambios' : 'Crear Proveedor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {selectedSupplier?.nombre_comercial}
            </DialogTitle>
          </DialogHeader>
          {selectedSupplier && (
            <div className="space-y-6">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Razón Social:</span>
                  <span className="ml-2 text-slate-900">{selectedSupplier.razon_social || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">NIT:</span>
                  <span className="ml-2 font-mono text-slate-900">{selectedSupplier.nit || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Teléfono:</span>
                  <span className="ml-2 text-slate-900">{selectedSupplier.telefono || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Email:</span>
                  <span className="ml-2 text-slate-900">{selectedSupplier.email || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Condición de pago:</span>
                  <Badge variant="outline" className="ml-2">{selectedSupplier.condicion_pago_label}</Badge>
                </div>
                <div>
                  <span className="text-slate-500">Límite crédito:</span>
                  <span className="ml-2 text-slate-900">
                    {selectedSupplier.limite_credito ? formatCurrency(selectedSupplier.limite_credito) : 'Sin límite'}
                  </span>
                </div>
              </div>

              {/* Balance Summary */}
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-amber-600">Saldo pendiente total</div>
                      <div className="text-2xl font-display font-medium text-amber-800">
                        {formatCurrency(selectedSupplier.saldo_pendiente_total)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-500">Total de compras</div>
                      <div className="text-xl font-medium text-slate-700">{selectedSupplier.total_compras}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pending Payables */}
              {supplierPayables.length > 0 && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">Cuentas por pagar pendientes</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left p-3">Orden</th>
                          <th className="text-left p-3">Fecha Venc.</th>
                          <th className="text-right p-3">Monto</th>
                          <th className="text-right p-3">Saldo</th>
                          <th className="text-center p-3">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierPayables.map(p => (
                          <tr key={p.id} className="border-b">
                            <td className="p-3 font-mono">{p.numero_orden}</td>
                            <td className="p-3">{p.fecha_vencimiento}</td>
                            <td className="p-3 text-right">{formatCurrency(p.monto_total)}</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(p.saldo_pendiente)}</td>
                            <td className="p-3 text-center">
                              <Badge variant="outline" className={
                                p.estado === 'vencida' ? 'border-red-300 text-red-700' :
                                p.estado === 'parcial' ? 'border-amber-300 text-amber-700' :
                                'border-slate-300 text-slate-700'
                              }>
                                {p.estado}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
